import { useState } from "react";
import { useLockBodyScroll } from "../../hooks/useLockBodyScroll";
import { useEscapeKey } from "../../hooks/useEscapeKey";
import { useThemeContext } from "../../theme/ThemeContext";
import { overlayBackdrop } from "../../theme/styles";
import { RADIUS } from "../../theme/tokens";
import { Button } from "../shared";
import { supabaseClient, dbLogSelfPasswordChange } from "../../db";
import { MODAL_CLOSE_DELAY_MS } from "../../utils/constants";

export function ChangePasswordModal({ sccoId, sccoName, onClose }) {
  const { SA, GLASS, tokens, TYPOGRAPHY } = useThemeContext();
  useLockBodyScroll();
  useEscapeKey(onClose);
  const [current,  setCurrent]  = useState("");
  const [next,     setNext]     = useState("");
  const [confirm,  setConfirm]  = useState("");
  const [error,    setError]    = useState("");
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);

  const pwRules = [
    { label: "8+ characters",      ok: next.length >= 8 },
    { label: "Uppercase letter",   ok: /[A-Z]/.test(next) },
    { label: "Lowercase letter",   ok: /[a-z]/.test(next) },
    { label: "Number",             ok: /[0-9]/.test(next) },
    { label: "Special character",  ok: /[^A-Za-z0-9]/.test(next) },
  ];
  const pwValid = pwRules.every(r => r.ok);

  const handleSave = async () => {
    setError("");
    if (!current.trim())  { setError("Enter your current password."); return; }
    if (!pwValid)         { setError("Password doesn't meet all requirements."); return; }
    if (next !== confirm) { setError("New passwords don't match."); return; }
    setSaving(true);
    const email = `scco_${sccoId}@lax.internal`;
    const { error: authError } = await supabaseClient.auth.signInWithPassword({ email, password: current });
    if (authError) { setError("Current password is incorrect."); setSaving(false); return; }
    await supabaseClient.auth.updateUser({ password: next });
    dbLogSelfPasswordChange(sccoName).catch(() => {});
    setSaved(true);
    setSaving(false);
    setTimeout(onClose, MODAL_CLOSE_DELAY_MS);
  };

  // Local input style — plain object (not the shared inputStyle function)
  const fieldInputStyle = {
    width: "100%", boxSizing: "border-box", background: SA.darkGray,
    border: `1px solid ${SA.borderGray}`, borderRadius: RADIUS.sm, color: SA.white,
    padding: "10px 12px", fontSize: TYPOGRAPHY.note, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
    outline: "none", letterSpacing: "0.06em",
  };

  return (
    <div onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }} style={{ ...overlayBackdrop, zIndex: 350,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
      <div onClick={e => e.stopPropagation()} style={{ ...GLASS.modal,
        width: "100%", maxWidth: 360,
        borderRadius: 16, display: "flex", flexDirection: "column" }}>

        {/* Header */}
        <div style={{ padding: "18px 24px 14px", borderBottom: `1px solid ${SA.borderGray}`,
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: SA.white }}>Change Password</div>
          <Button variant="icon" onClick={onClose}
            style={{ background: SA.medGray, color: SA.silver, width: 28, height: 28, fontSize: 16 }}>×</Button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver, marginBottom: 6, fontWeight: 500 }}>Current password</label>
            <input type="password" value={current} onChange={e => { setCurrent(e.target.value); setError(""); }}
              onKeyDown={e => e.key === "Enter" && handleSave()}
              style={fieldInputStyle} />
          </div>

          <div style={{ marginBottom: 6 }}>
            <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver, marginBottom: 6, fontWeight: 500 }}>New password</label>
            <input type="password" value={next} onChange={e => { setNext(e.target.value); setError(""); }}
              onKeyDown={e => e.key === "Enter" && handleSave()}
              style={fieldInputStyle} />
          </div>

          {/* Live requirements checklist — only shown once the user starts typing */}
          {next.length > 0 && (
            <div style={{ marginBottom: 14, padding: "10px 12px", borderRadius: RADIUS.sm,
              background: "rgba(255,255,255,0.04)", border: `1px solid ${SA.borderGray}` }}>
              {pwRules.map(r => (
                <div key={r.label} style={{ display: "flex", alignItems: "center", gap: 8,
                  fontSize: TYPOGRAPHY.caption, lineHeight: "22px", color: r.ok ? tokens.success : SA.silver }}>
                  <span style={{ fontSize: TYPOGRAPHY.note, width: 14, textAlign: "center", flexShrink: 0 }}>
                    {r.ok ? "✓" : "✗"}
                  </span>
                  {r.label}
                </div>
              ))}
            </div>
          )}

          <div style={{ marginBottom: 14 }}>
            <label style={{ display: "block", fontSize: TYPOGRAPHY.caption, color: SA.silver, marginBottom: 6, fontWeight: 500 }}>Confirm new</label>
            <input type="password" value={confirm} onChange={e => { setConfirm(e.target.value); setError(""); }}
              onKeyDown={e => e.key === "Enter" && handleSave()}
              style={{ ...fieldInputStyle, borderColor: confirm && next && confirm !== next ? tokens.danger : SA.borderGray }} />
          </div>

          {error && (
            <div style={{ fontSize: TYPOGRAPHY.body, color: tokens.danger, marginBottom: 14, fontWeight: 500 }}>{error}</div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 24px 16px", borderTop: `1px solid ${SA.borderGray}` }}>
          <Button variant="primary" fullWidth onClick={handleSave} disabled={saving || saved}
            style={{ padding: "13px", borderRadius: 12, fontSize: TYPOGRAPHY.note, fontWeight: 600,
              ...(saved ? { background: "rgba(110,196,114,0.15)", border: "1px solid #2E6B30" } : {}),
              color: saved ? tokens.success : "#fff",
              transition: "all 0.2s", opacity: saving ? 0.7 : 1 }}>
            {saved ? "✓ Password updated" : saving ? "Saving…" : "Update Password"}
          </Button>
        </div>
      </div>
    </div>
  );
}
